import ApplyV2Select from '@core/components/ApplyDialogV2/ApplyV2Select'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import React, { useId } from 'react'
import clsx from 'clsx'
import WarningErrorIcon from '@core/components/icons/WarningErrorIcon'
import './ApplyV2DateSelectGroup.scss'

interface Props {
    label?: string
    names?: {
        day?: string
        month?: string
        year?: string
    }
    value: {
        day?: string | number | null
        month?: string | number | null
        year?: string | number | null
    }
    errors?: {
        day?: string | null | boolean
        month?: string | null | boolean
        year?: string | null | boolean
    }
    noExpiryChecked?: boolean
    onChange?: (date: {
        day?: string | number | null
        month?: string | number | null
        year?: string | number | null
    }) => void
    isDisabled?: boolean
    className?: string
    onFocus?: React.FocusEventHandler<HTMLDivElement>
    onBlur?: React.FocusEventHandler<HTMLDivElement>
    id?: string
    name?: string
    // errorMessage?: string | null | boolean
    isRequired?: boolean
    isShowErrorMessage?: boolean
    isShowExpirationHeader?: boolean
    isLoading?: boolean
}

const baseClass = 'ApplyV2DateSelectGroup'

const months = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
]

const currentYear = new Date().getFullYear()
const years = Array.from({ length: 20 }, (_, i) => ({
    value: (currentYear + i).toString(),
    label: (currentYear + i).toString(),
}))

const days = Array.from({ length: 31 }, (_, i) => ({
    value: (i + 1).toString().padStart(2, '0'),
    label: (i + 1).toString().padStart(2, '0'),
}))

function ApplyV2DateSelectGroup({
    label,
    value = { day: null, month: null, year: null },
    noExpiryChecked = false,
    onFocus,
    onBlur,
    onChange = () => {},
    isDisabled = false,
    className = '',
    id,
    name,
    isRequired = false,
    isShowErrorMessage = true,
    isShowExpirationHeader = false,
    isLoading = false,
    names,
    errors,
}: Props) {
    const defaultId = useId()
    const groupId = id || defaultId

    const errorMessage = [...new Set([errors?.day, errors?.month, errors?.year].filter(Boolean))].join(', ')

    return (
        <div
            className={clsx(baseClass, className, {
                [`${baseClass}--error`]: !!errorMessage,
                [`${baseClass}--disabled`]: isDisabled,
                [`${baseClass}--loading`]: isLoading,
            })}
            onFocus={onFocus}
            tabIndex={-1}
        >
            {(label ?? isShowExpirationHeader) && (
                <div
                    className={clsx(`${baseClass}__header`, {
                        [`${className}__header`]: !!className,
                    })}
                >
                    <div
                        className={clsx(`${baseClass}__header--title`, {
                            [`${className}__header--title`]: !!className,
                        })}
                    >
                        {decodeHtmlEntitiesRecursive(label)}
                        {isRequired && '*'}
                    </div>
                </div>
            )}

            <div className={`${baseClass}__date-fields`}>
                <ApplyV2Select
                    id={`${groupId}-day`}
                    name={names?.day || 'day'}
                    options={days}
                    placeholder="DD"
                    isLoading={isLoading}
                    className={clsx(`${baseClass}__select`, {
                        [`${className}__select`]: !!className,
                    })}
                    errorMessage={errors?.day}
                    isShowErrorMessage={false}
                    onChange={(v) => {
                        onChange({
                            day: v,
                            month: value ? value.month : null,
                            year: value ? value.year : null,
                        })
                    }}
                    onBlur={onBlur}
                />

                <ApplyV2Select
                    id={`${groupId}-month`}
                    name={names?.month || 'month'}
                    options={months}
                    placeholder="Month"
                    isLoading={isLoading}
                    className={clsx(`${baseClass}__select`, `${baseClass}__select-month`, {
                        [`${className}__select`]: !!className,
                        [`${className}__select-month`]: !!className,
                    })}
                    errorMessage={errors?.month}
                    isShowErrorMessage={false}
                    onChange={(v) => {
                        onChange({
                            day: value ? value.day : null,
                            month: v,
                            year: value ? value.year : null,
                        })
                    }}
                    onBlur={onBlur}
                />

                <ApplyV2Select
                    id={`${groupId}-year`}
                    name={names?.year || 'year'}
                    options={years}
                    placeholder="YYYY"
                    isLoading={isLoading}
                    className={clsx(`${baseClass}__select`, {
                        [`${className}__select`]: !!className,
                    })}
                    errorMessage={errors?.year}
                    isShowErrorMessage={false}
                    onChange={(v) => {
                        onChange({
                            day: value ? value.day : null,
                            month: value ? value.month : null,
                            year: v,
                        })
                    }}
                    onBlur={onBlur}
                />
            </div>

            {errorMessage && isShowErrorMessage && (
                <div className={`${baseClass}__error`}>
                    <div className={`${baseClass}__error--icon`}>
                        <WarningErrorIcon />
                    </div>
                    <div className={`${baseClass}__error--text`}>{errorMessage}</div>
                </div>
            )}
        </div>
    )
}

export default ApplyV2DateSelectGroup
